/**
 * 模板发现器
 * 
 * 扫描模板目录，解析 _CONFIG.md 获取元数据
 * 只负责发现和读取，不做任何"智能"处理
 * 
 * @module core/templates/discovery
 */

import * as fs from 'fs';
import * as path from 'path';
import { 
    TemplateMetadata, 
    TemplateType, 
    TemplateFile, 
    TemplateResult,
    TemplateListItem 
} from './types.js';
import { ConsoleLogger } from '../types';

/**
 * 模板根目录
 */
const TEMPLATES_DIR = path.resolve(
    path.dirname(new URL(import.meta.url).pathname),
    '../../../../templates'
);

/**
 * 模板缓存
 */
let templatesCache: Map<string, TemplateMetadata> | null = null;

/**
 * 确保缓存已加载
 */
function ensureCache(): void {
    if (templatesCache) return;
    
    templatesCache = new Map();
    scanTemplates(TEMPLATES_DIR, '');
}

/**
 * 递归扫描模板目录
 */
function scanTemplates(dir: string, prefix: string): void {
    if (!fs.existsSync(dir)) return;
    
    const entries = fs.readdirSync(dir, { withFileTypes: true });
    
    for (const entry of entries) {
        if (!entry.isDirectory()) continue;
        if (entry.name.startsWith('.') || entry.name === 'node_modules') continue;
        
        const fullPath = path.join(dir, entry.name);
        const templateId = prefix ? `${prefix}/${entry.name}` : entry.name;
        
        // 检查是否有 _CONFIG.md（表示这是一个模板目录）
        const configPath = path.join(fullPath, '_CONFIG.md');
        if (fs.existsSync(configPath)) {
            const metadata = parseConfigMd(configPath, templateId, fullPath);
            if (metadata) {
                templatesCache!.set(templateId, metadata);
            }
        }
        
        // 继续递归扫描子目录
        scanTemplates(fullPath, templateId);
    }
}

/**
 * 解析 _CONFIG.md 获取模板元数据
 */
function parseConfigMd(configPath: string, templateId: string, templateDir: string): TemplateMetadata | null {
    try {
        const content = fs.readFileSync(configPath, 'utf-8');
        
        // 提取标题作为名称
        const titleMatch = content.match(/^#\s+(.+)$/m);
        const name = titleMatch ? titleMatch[1].trim() : templateId;
        
        // 提取描述（第一个 > 引用块）
        const descMatch = content.match(/^>\s*(.+)$/m);
        const description = descMatch ? descMatch[1].trim() : '';
        
        // 提取适用框架
        const frameworkMatch = content.match(/\*\*适用框架\*\*:\s*(.+)/i);
        const frameworks = frameworkMatch 
            ? frameworkMatch[1].split(/[,\/+]/).map(f => f.trim())
            : [];
        
        // 推断模板类型
        const type = inferTemplateType(templateId, name);
        
        // 获取文件列表（排除 _CONFIG.md 和 _template.json）
        const files = getTemplateFiles(templateDir);
        
        // 提取依赖
        const dependencies = extractDependencies(content);
        
        // 提取建议路径
        const pathMatch = content.match(/复制到.*?`([^`]+)`/i);
        const suggestedPath = pathMatch ? pathMatch[1] : undefined;
        
        return {
            id: templateId,
            name,
            description,
            frameworks,
            type,
            files,
            dependencies,
            configFile: '_CONFIG.md',
            suggestedPath,
            keywords: extractKeywords(name, description, frameworks)
        };
    } catch (error) {
        console.error(`解析模板配置失败: ${configPath}`, error);
        return null;
    }
}

/**
 * 推断模板类型
 */
function inferTemplateType(templateId: string, name: string): TemplateType {
    const lower = (templateId + name).toLowerCase();
    
    if (lower.includes('api') || lower.includes('request') || lower.includes('axios')) {
        return 'api-layer';
    }
    if (lower.includes('component')) return 'component';
    if (lower.includes('store') || lower.includes('pinia')) return 'store';
    if (lower.includes('composable') || lower.includes('hook')) return 'composable';
    if (lower.includes('config')) return 'config';
    if (lower.includes('type')) return 'types';
    
    return 'other';
}

/**
 * 获取模板目录下的所有文件
 */
function getTemplateFiles(dir: string, prefix: string = ''): string[] {
    const files: string[] = [];
    
    const entries = fs.readdirSync(dir, { withFileTypes: true });
    for (const entry of entries) {
        const relativePath = prefix ? `${prefix}/${entry.name}` : entry.name;
        
        // 跳过配置文件
        if (entry.name === '_CONFIG.md' || entry.name === '_template.json') {
            continue;
        }
        
        if (entry.isDirectory()) {
            files.push(...getTemplateFiles(path.join(dir, entry.name), relativePath));
        } else {
            files.push(relativePath);
        }
    }
    
    return files;
}

/**
 * 从 _CONFIG.md 提取依赖
 */
function extractDependencies(content: string): Record<string, string> | undefined {
    const depsMatch = content.match(/"dependencies":\s*\{([^}]+)\}/);
    if (!depsMatch) return undefined;
    
    const deps: Record<string, string> = {};
    const pairs = depsMatch[1].matchAll(/"([^"]+)":\s*"([^"]+)"/g);
    
    for (const match of pairs) {
        deps[match[1]] = match[2];
    }
    
    return Object.keys(deps).length > 0 ? deps : undefined;
}

/**
 * 提取关键词
 */
function extractKeywords(name: string, description: string, frameworks: string[]): string[] {
    const text = `${name} ${description}`.toLowerCase();
    const keywords = new Set<string>(frameworks.map(f => f.toLowerCase()));
    
    // 添加常见关键词
    const commonKeywords = ['axios', 'api', 'mock', 'request', 'http', 'fetch', 'store', 'pinia', 'vuex'];
    for (const kw of commonKeywords) {
        if (text.includes(kw)) keywords.add(kw);
    }
    
    return Array.from(keywords);
}

/**
 * 列出所有可用模板
 */
export function listAllTemplates(): TemplateListItem[] {
    ensureCache();
    
    const result: TemplateListItem[] = [];
    
    for (const [id, metadata] of templatesCache!) {
        result.push({
            id,
            name: metadata.name,
            description: metadata.description,
            frameworks: metadata.frameworks,
            type: metadata.type,
            fileCount: metadata.files.length,
            suggestedPath: metadata.suggestedPath
        });
    }
    
    return result;
}

/**
 * 按类型筛选模板
 */
export function listTemplatesByType(type: TemplateType): TemplateListItem[] {
    return listAllTemplates().filter(t => t.type === type);
}

/**
 * 按框架筛选模板
 */
export function listTemplatesByFramework(framework: string): TemplateListItem[] {
    const lower = framework.toLowerCase();
    return listAllTemplates().filter(t => 
        t.frameworks.some(f => f.toLowerCase().includes(lower))
    );
}

/**
 * 获取模板详情
 * 
 * @param templateId 模板 ID
 * @param includeFiles 是否包含文件内容（默认 false，只返回配置说明）
 */
export function getTemplateById(templateId: string, includeFiles: boolean = false): TemplateResult | null {
    ensureCache();
    
    const metadata = templatesCache!.get(templateId);
    if (!metadata) return null;
    
    const templateDir = path.join(TEMPLATES_DIR, templateId);
    
    // 读取配置说明
    const configPath = path.join(templateDir, '_CONFIG.md');
    const configGuide = fs.existsSync(configPath) 
        ? fs.readFileSync(configPath, 'utf-8')
        : '';
    
    const result: TemplateResult = {
        metadata,
        configGuide
    };
    
    // 如果需要完整文件内容
    if (includeFiles) {
        result.files = [];
        
        for (const filePath of metadata.files) {
            const fullPath = path.join(templateDir, filePath);
            if (fs.existsSync(fullPath)) {
                result.files.push({
                    path: filePath,
                    content: fs.readFileSync(fullPath, 'utf-8'),
                    isConfig: filePath.startsWith('_')
                });
            }
        }
    }
    
    return result;
}

/**
 * 获取模板目录的绝对路径
 */
export function getTemplateDir(templateId: string): string | null {
    ensureCache();
    
    if (!templatesCache!.has(templateId)) return null;
    
    return path.join(TEMPLATES_DIR, templateId);
}

/**
 * 搜索模板
 */
export function searchTemplates(query: string): TemplateListItem[] {
    const lower = query.toLowerCase();
    
    return listAllTemplates().filter(t => 
        t.id.toLowerCase().includes(lower) ||
        t.name.toLowerCase().includes(lower) ||
        t.description.toLowerCase().includes(lower) ||
        t.frameworks.some(f => f.toLowerCase().includes(lower))
    );
}

/**
 * 刷新缓存
 */
export function refreshCache(): void {
    templatesCache = null;
    ensureCache();
}
